import store from '../store'
import { ErrorType, pushException } from '../reducers/errorReducer'
import { stripHtml } from 'string-strip-html'
import { isAppContext } from './appHelper'
import { AppContext } from '../config/appConfig'

export type MessageAttachment = {
    type: string
    name: string
    url?: string
    isInline?: boolean
    itemId?: string
}

export type EmailAddressDetails = Partial<Office.EmailAddressDetails> & Required<Pick<Office.EmailAddressDetails, 'emailAddress'>>

export type MessageForm = {
    //An array of EmailAddressDetails object for each of the recipients on the To line. The array is limited to a maximum of 100 entries.
    toRecipients: EmailAddressDetails[]
    //An array of EmailAddressDetails object for each of the recipients on the Cc line. The array is limited to a maximum of 100 entries.
    ccRecipients?: EmailAddressDetails[]
    //An array of EmailAddressDetails object for each of the recipients on the Bcc line. The array is limited to a maximum of 100 entries.
    bccRecipients?: EmailAddressDetails[]
    //A string containing the subject of the message. The string is limited to a maximum of 255 characters.
    subject?: string
    //The HTML body of the message. The body content is limited to a maximum size of 32 KB.
    htmlBody?: string
    //An array of JSON objects that are either file or item attachments.
    attachments?: MessageAttachment[]
}

export type AppointmentForm = Partial<Omit<Office.AppointmentForm, 'requiredAttendees' | 'optionalAttendees' | 'subject' | 'body'>> & {
    //An array of EmailAddressDetails object for each of the attendees on the required line. The array is limited to a maximum of 100 entries.
    requiredAttendees: EmailAddressDetails[]
    //An array of EmailAddressDetails object for each of the attendees on the optional line. The array is limited to a maximum of 100 entries.
    optionalAttendees?: EmailAddressDetails[]
    //A string containing the subject of the message. The string is limited to a maximum of 255 characters.
    subject?: string
    //The body of the message. The body content is limited to a maximum size of 32 KB.
    body?: string
}

export function outlookDisplayNewMessageForm(parameters: MessageForm, callback?: (asyncResult?: Office.AsyncResult<void>) => void): void {
    try {
        if ((!isOfficeInitialized() || isAppContext([AppContext.Compose])) && !(parameters.attachments?.length > 0)) {
            parameters.htmlBody =
                parameters.htmlBody &&
                stripHtml(parameters.htmlBody, {
                    dumpLinkHrefsNearby: {
                        enabled: true,
                        putOnNewLine: false,
                        wrapHeads: '[',
                        wrapTails: ']',
                    },
                }).result

            window.location.href = [
                `mailto:${parameters.toRecipients.map(att => att.emailAddress).join(';')}`,
                parameters.bccRecipients ? `&bcc=${parameters.bccRecipients.map(att => att.emailAddress).join(';')}` : undefined,
                `?subject=${encodeURIComponent(parameters.subject) || ''}`,
                parameters.htmlBody ? `&body=${encodeURIComponent(parameters.htmlBody)}` : undefined,
            ]
                .filter(value => value !== undefined)
                .join('')

            callback?.(undefined)
        } else {
            assertOfficeInitialized()

            parameters.subject = parameters.subject?.substring(0, Math.min(parameters.subject.length, 255))
            parameters.htmlBody = parameters.htmlBody?.replaceAll('\n', '<br/>')
            parameters.htmlBody = parameters.htmlBody?.substring(0, Math.min(parameters.htmlBody?.length, 16380))

            if (Office.context.requirements.isSetSupported('Mailbox', '1.9') && Office.context.mailbox.displayNewMessageFormAsync) {
                Office.context.mailbox.displayNewMessageFormAsync(parameters, result => {
                    console.log('displayNewMessageFormAsync: ', result.error)
                    callback?.(result)
                })
            } else {
                Office.context.mailbox.displayNewMessageForm(parameters)
                callback?.(undefined)
            }
        }
    } catch (e) {
        console.log('displayNewMessageForm: ', e)
        store.dispatch(pushException({ id: e.message, type: ErrorType.Outlook, message: e.message }, e))
    }
}

export function outlookDisplayNewAppointmentForm(parameters: AppointmentForm, callback?: (asyncResult?: Office.AsyncResult<void>) => void): void {
    try {
        if (!isOfficeInitialized() || isAppContext([AppContext.Compose])) {
            window.location.href = [
                `webcal:${parameters.requiredAttendees.map(att => att.emailAddress).join(';')}`,
                `?subject=${encodeURIComponent(parameters.subject) || ''}`,
            ]
                .filter(value => value !== undefined)
                .join('')

            callback?.(undefined)
        } else {
            assertOfficeInitialized()

            parameters.subject = parameters.subject?.substring(0, Math.min(parameters.subject.length, 255))
            parameters.body = parameters.body?.replaceAll('\n', '<br/>')
            parameters.body = parameters.body?.substring(0, Math.min(parameters.body?.length, 16380))

            if (Office.context.requirements.isSetSupported('Mailbox', '1.9') && Office.context.mailbox.displayNewAppointmentFormAsync) {
                Office.context.mailbox.displayNewAppointmentFormAsync(parameters as Office.AppointmentForm, result => {
                    console.log('displayNewAppointmentFormAsync: ', result.error)
                    callback?.(result)
                })
            } else {
                Office.context.mailbox.displayNewAppointmentForm(parameters as Office.AppointmentForm)
                callback?.(undefined)
            }
        }
    } catch (e) {
        console.log('displayNewAppointmentForm: ', e)
        store.dispatch(pushException({ id: e.message, type: ErrorType.Outlook, message: e.message }, e))
    }
}

export function outlookDisplayMessageForm(messageId: string, callback?: (asyncResult?: Office.AsyncResult<void>) => void): void {
    try {
        if (isOfficeInitialized() && messageId) {
            if (Office.context.requirements.isSetSupported('Mailbox', '1.9') && Office.context.mailbox.displayMessageFormAsync) {
                Office.context.mailbox.displayMessageFormAsync(messageId, result => {
                    console.log('outlookDisplayMessageForm: ', result.error)
                    callback?.(result)
                })
            } else {
                Office.context.mailbox.displayMessageForm(messageId)
                callback?.(undefined)
            }
        }
    } catch (e) {
        console.log('displayMessageForm: ', e)
        store.dispatch(pushException({ id: e.message, type: ErrorType.Outlook, message: e.message }, e))
    }
}

export function isOfficeInitialized(): boolean {
    return Office.hasOwnProperty('context') && Office.context.hasOwnProperty('ui')
}

export function isOfficeApplication(): boolean {
    return isOfficeInitialized() && [Office.PlatformType.PC, Office.PlatformType.Mac].includes(Office.context.platform)
}

export function assertOfficeInitialized(): void {
    if (!isOfficeInitialized()) {
        throw new Error('Deze handeling kan alleen vanuit Outlook worden uitgevoerd.')
    }
}
